import {
  AlertTriangle,
  ArrowRight,
  HeartHandshake,
  Home,
  UserRound,
} from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardPriority } from "@/api/dashboard";
import { Card } from "@/components/ui/card";

type Props = {
  priorities: DashboardPriority[];
};

export function PriorityCenterCard({
  priorities,
}: Props) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Priority Center
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Highest priority follow-ups
          </p>
        </div>

        <Link
          to="/intelligence"
          className="text-sm font-medium text-violet-400 transition hover:text-violet-300"
        >
          View all
        </Link>
      </div>

      {priorities.length === 0 ? (
        <div className="p-8 text-center">
          <p className="font-medium text-white">
            No immediate priorities
          </p>

          <p className="mt-2 text-sm text-slate-400">
            MinistryIQ has not identified any urgent
            follow-up.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {priorities.map((priority) => {
            const visual = getVisual(priority);
            const descriptionLines =
              splitDescription(priority.description);

            return (
              <Link
                key={priority.id}
                to={priority.to}
                className={`
                  group relative block overflow-hidden px-5 py-4
                  transition-all duration-200
                  hover:bg-slate-800/40
                  ${getSeverityBorder(priority.severity)}
                `}
              >
                <div className="flex items-start gap-4 transition-transform duration-200 group-hover:translate-x-1">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition group-hover:scale-105 ${visual.iconClass}`}
                  >
                    {visual.icon}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <p className="font-semibold text-white">
                        {priority.title}
                      </p>

                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${visual.badgeClass}`}
                      >
                        {formatSeverity(priority.severity)}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      {descriptionLines.map(
                        (line, index) => (
                          <p
                            key={`${priority.id}-${index}`}
                            className="flex items-start gap-2 text-sm leading-5 text-slate-400"
                          >
                            <span
                              aria-hidden="true"
                              className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600"
                            />

                            <span>{line}</span>
                          </p>
                        ),
                      )}
                    </div>

                    <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-500/10 px-3 py-2 text-sm font-medium text-indigo-300 transition group-hover:bg-indigo-500/15">
                      <ArrowRight
                        size={15}
                        className="transition-transform group-hover:translate-x-1"
                      />

                      {priority.recommendation}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function splitDescription(
  description: string,
): string[] {
  const normalized = description
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) {
    return [];
  }

  const withoutPrefixes = normalized
    .replace(
      /^Risk Score:\s*\d+(?:\/\d+)?\.?\s*/i,
      "",
    )
    .replace(
      /^Risk Level:\s*[A-Z_]+\.?\s*/i,
      "",
    )
    .replace(/^Reasons:\s*/i, "");

  const lines = withoutPrefixes
    .split(/(?:;\s*|\.\s+)/)
    .map((line) =>
      line
        .replace(/^Reasons:\s*/i, "")
        .replace(/[.;,\s]+$/, "")
        .trim(),
    )
    .filter(Boolean);

  return lines.length > 0
    ? lines
    : [normalized];
}

function formatSeverity(
  severity: DashboardPriority["severity"],
) {
  return severity
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
}

function getVisual(
  priority: DashboardPriority,
) {
  const normalized =
    priority.entity_type.toUpperCase();

  const icon =
    normalized === "HOUSEHOLD" ? (
      <Home size={19} />
    ) : normalized === "CARE" ? (
      <HeartHandshake size={19} />
    ) : (
      <UserRound size={19} />
    );

  if (priority.severity === "CRITICAL") {
    return {
      icon: <AlertTriangle size={19} />,
      iconClass:
        "bg-rose-500/15 text-rose-400",
      badgeClass:
        "bg-rose-500/15 text-rose-300",
    };
  }

  return {
    icon,
    iconClass:
      "bg-amber-500/15 text-amber-400",
    badgeClass:
      "bg-amber-500/15 text-amber-300",
  };
}

function getSeverityBorder(
  severity: DashboardPriority["severity"],
) {
  switch (severity) {
    case "CRITICAL":
      return "border-l-4 border-l-rose-500";

    case "WARNING":
      return "border-l-4 border-l-amber-500";

    case "WATCH":
      return "border-l-4 border-l-yellow-400";

    default:
      return "border-l-4 border-l-blue-500";
  }
}
